import React, { useState } from 'react';
import { xeroAPI } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Search, Loader2, Link2, CheckCircle2 } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const STATUS_CLS = {
  PAID: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  AUTHORISED: 'bg-blue-100 text-blue-700 border-blue-200',
  SUBMITTED: 'bg-blue-100 text-blue-700 border-blue-200',
  DRAFT: 'bg-slate-100 text-slate-600 border-slate-200',
};

export const LinkXeroInvoiceDialog = ({ customerId, open, onOpenChange, onLinked }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [linkingId, setLinkingId] = useState(null);

  const doSearch = async () => {
    if (query.trim().length < 2) { toast.error('Enter at least 2 characters'); return; }
    setSearching(true);
    setSearched(true);
    try {
      const res = await xeroAPI.searchInvoices(query.trim());
      setResults(res.data?.invoices || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not search Xero');
      setResults([]);
    } finally { setSearching(false); }
  };

  const link = async (inv) => {
    setLinkingId(inv.xero_invoice_id);
    try {
      await xeroAPI.linkInvoice(customerId, { xero_invoice_id: inv.xero_invoice_id, invoice_type: 'custom' });
      toast.success(`Linked ${inv.invoice_number || 'invoice'} to this customer`);
      // mark it linked locally so the button updates
      setResults((prev) => prev.map((r) => r.xero_invoice_id === inv.xero_invoice_id ? { ...r, already_linked: true } : r));
      onLinked?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not link invoice');
    } finally { setLinkingId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="link-xero-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-blue-600" /> Add existing Xero invoice</DialogTitle>
          <DialogDescription>
            Search Xero by invoice number or customer name, then link the invoice to this customer's account.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doSearch()}
            placeholder="Invoice number or customer name…"
            data-testid="link-xero-search-input"
            autoFocus
          />
          <Button onClick={doSearch} disabled={searching} className="bg-blue-600 hover:bg-blue-700 flex-shrink-0" data-testid="link-xero-search-btn">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-1.5">Search</span>
          </Button>
        </div>

        <div className="max-h-[50vh] overflow-y-auto space-y-2 mt-1">
          {searching ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
          ) : searched && results.length === 0 ? (
            <p className="text-sm text-slate-400 italic text-center py-8" data-testid="link-xero-empty">No matching invoices found in Xero.</p>
          ) : results.map((inv) => (
            <div key={inv.xero_invoice_id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`link-xero-result-${inv.xero_invoice_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900">{inv.invoice_number || '(no number)'}</span>
                  <Badge variant="outline" className={STATUS_CLS[inv.status] || 'bg-slate-100 text-slate-600'}>{inv.status}</Badge>
                </div>
                <p className="text-xs text-slate-500 truncate">{inv.contact_name || 'No contact'}{inv.date ? ` · ${formatDate(inv.date)}` : ''}</p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-sm font-semibold text-slate-800">{formatCurrency(inv.total)}</span>
                {inv.already_linked ? (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`link-xero-linked-${inv.xero_invoice_id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Linked
                  </Badge>
                ) : (
                  <Button size="sm" onClick={() => link(inv)} disabled={linkingId === inv.xero_invoice_id} className="bg-blue-600 hover:bg-blue-700" data-testid={`link-xero-add-${inv.xero_invoice_id}`}>
                    {linkingId === inv.xero_invoice_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                    <span className="ml-1">Link</span>
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkXeroInvoiceDialog;
